import { withStaticSpan, type BrowserOptions } from "@sentry/react";
import { cutUrlQueries } from "./sentryUrlCut";

export interface BrowserSentryEnv {
    MODE: string;
    VITE_OPTIVO_RUNTIME_ENV?: string;
    VITE_SENTRY_DSN?: string;
}

const DIGITALOCEAN_RUNTIME = "digitalocean";

/**
 * Whatever this leaves out, the SDK collects, so every field is required: one a
 * later version adds fails the build here instead of defaulting to on. It is
 * also what keeps the client IP address from being inferred, and it would undo
 * `piiSanitizer.ts`, which strips guest names, e-mails and phone numbers from
 * reservations on ingest, if the browser shipped the same data back out in an
 * error payload. The service's copy is `DATA_COLLECTION` in
 * `optivo-service/src/sentryConfig.ts`.
 */
const DATA_COLLECTION: Required<NonNullable<BrowserOptions["dataCollection"]>> = {
    userInfo: false,
    cookies: false,
    httpHeaders: false,
    httpBodies: [],
    urlQueryParams: false,
    graphQL: { document: false, variables: false },
    genAI: { inputs: false, outputs: false },
    mcp: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    stackFrameVariables: false,
    frameContextLines: 7,
};

/** The parts of an event's `request` that could say who sent it or what they asked. */
interface EventWithRequest {
    request?: {
        url?: string;
        headers?: unknown;
        cookies?: unknown;
        data?: unknown;
        query_string?: unknown;
    };
}

/**
 * The last word before an event leaves the browser. The SDK's default HTTP
 * context integration puts the page's full URL — its query string included —
 * and the `User-Agent` and `Referer` headers on every error and transaction,
 * whatever `dataCollection` says. The privacy notice tells customers error
 * reports carry no request headers or request content, so the headers go (and
 * anything else a later SDK attaches about the request) and the URL is cut to
 * its path: which screen failed, never what was in its query or fragment. Any
 * other URL the event holds goes the same way, a span's, an error message's, a
 * console line's: `/reset-password?token=…` is a page's URL too.
 * The server's twin is `stripRequestContent` in `optivo-service/src/sentryConfig.ts`.
 */
export function stripBrowserRequest<T extends EventWithRequest>(event: T): T {
    if (event.request) {
        delete event.request.headers;
        delete event.request.cookies;
        delete event.request.data;
        delete event.request.query_string;
        if (event.request.url) event.request.url = toPath(event.request.url);
    }
    return cutUrlQueries(event);
}

/** A URL, absolute or relative, without its query string or fragment. */
function toPath(url: string): string {
    return url.split(/[?#]/)[0];
}

/** A message that is a URL and nothing else: absolute, or a path from the root. */
const URL_MESSAGE = /^(?:https?:\/\/|\/)\S*$/;

/** The parts of a breadcrumb that can carry a URL. */
interface BreadcrumbLike {
    message?: string;
    data?: Record<string, unknown>;
}

/** Where the default breadcrumbs put a URL: a request's `url`, a navigation's `from` and `to`. */
const BREADCRUMB_URL_FIELDS = ["url", "from", "to"] as const;

/**
 * Every breadcrumb, as it is recorded. The default ones name each page the
 * operator moved between (`navigation`: `from`, `to`) and each request the
 * page made (`fetch`, `xhr`: `url`), each with its query string and fragment:
 * a property's id, a night, a search typed into an admin list. They travel
 * with the next error, so each is cut to its path, and so is a message that
 * is a URL. The breadcrumb is copied, never changed: a failed fetch's
 * breadcrumb carries the SDK's own request data, which it reads again.
 */
export function cutBrowserBreadcrumb<T extends BreadcrumbLike>(breadcrumb: T): T {
    let cut = breadcrumb;
    if (breadcrumb.data) {
        const data = { ...breadcrumb.data };
        let changed = false;
        for (const field of BREADCRUMB_URL_FIELDS) {
            const value = data[field];
            if (typeof value === "string" && toPath(value) !== value) {
                data[field] = toPath(value);
                changed = true;
            }
        }
        if (changed) cut = { ...cut, data };
    }
    const message = breadcrumb.message;
    if (typeof message === "string" && URL_MESSAGE.test(message) && toPath(message) !== message) {
        cut = { ...cut, message: toPath(message) };
    }
    return cut;
}

/** The parts of a span that can carry a URL. */
interface SpanLike {
    op?: string;
    description?: string;
    data?: Record<string, unknown>;
}

/**
 * A span's URL attributes, the SDK's and OpenTelemetry's names alike: cut to
 * their path. The pageload span names the largest paint's resource by URL
 * (`lcp.url`), and a web-vital span the element's.
 */
const SPAN_URL_ATTRIBUTES = ["url", "http.url", "url.full", "lcp.url", "browser.web_vital.lcp.url", "ui.element.url"] as const;
/**
 * Dropped: a span's query and fragment attributes, and the browser's
 * user-agent string an interaction's web-vital span carries, which is a
 * request header under another name.
 */
const SPAN_DROPPED_ATTRIBUTES = ["http.query", "http.fragment", "url.query", "url.fragment", "user_agent.original"] as const;

/**
 * Every span a sampled transaction sends (`main.tsx` turns tracing on). A
 * request's span carries its URL, its query and its fragment as attributes,
 * and a resource's span is named by its URL: the query and fragment go, and
 * every URL is cut to its path. A span of any other kind keeps its name, a URL
 * in it cut: the seven `browser.*` spans of a page load are named by the
 * page's full URL.
 */
export function cutBrowserSpan<T extends SpanLike>(span: T): T {
    let cut = span;
    if (span.data) {
        const data = { ...span.data };
        for (const key of SPAN_DROPPED_ATTRIBUTES) delete data[key];
        for (const key of SPAN_URL_ATTRIBUTES) {
            const value = data[key];
            if (typeof value === "string") data[key] = toPath(value);
        }
        cut = { ...cut, data };
    }
    if (typeof span.description === "string") {
        const named = span.op?.startsWith("http.") || span.op?.startsWith("resource.");
        cut = { ...cut, description: named ? toPath(span.description) : cutUrlQueries(span.description) };
    }
    return cut;
}

export function getBrowserSentryConfig(env: BrowserSentryEnv) {
    const isDigitalOceanProduction = env.MODE === "production"
        && env.VITE_OPTIVO_RUNTIME_ENV === DIGITALOCEAN_RUNTIME
        && Boolean(env.VITE_SENTRY_DSN);

    return {
        dsn: env.VITE_SENTRY_DSN,
        environment: isDigitalOceanProduction ? "digitalocean-production" : env.MODE,
        enabled: isDigitalOceanProduction,
        dataCollection: DATA_COLLECTION,
        beforeSend: stripBrowserRequest,
        beforeSendTransaction: stripBrowserRequest,
        beforeBreadcrumb: cutBrowserBreadcrumb,
        beforeSendSpan: withStaticSpan(cutBrowserSpan),
        /**
         * `beforeSendTransaction` runs on this lifecycle alone, and a span hook
         * not wrapped for the lifecycle in force is skipped without a word: on
         * the SDK's default, streamed spans would leave uncut, a reset link's
         * token in the name of each.
         */
        traceLifecycle: "static" as const,
        /** A thrown error has its stack; a message or a plain value needs none. */
        attachStacktrace: false,
    };
}
